import { useState, useEffect, useMemo, useCallback } from "react";
import {
  Star, Filter, Shuffle, X, ChevronLeft, ChevronRight,
} from "lucide-react";
import { generateFlashcardsForSession, fetchLatestFlashcards, isFlashcardsRequestInFlight } from "../../services/flashcardService";
import useLimits from "../../hooks/useLimits"; // <-- added


const FlashcardSkeleton = () => (
  <div className="w-full max-w-3xl animate-pulse">
    <div className="bg-gray-200 rounded-xl shadow-md w-full" style={{ minHeight: "550px" }} />
    <div className="flex items-center justify-between mt-6">
      <div className="h-12 w-12 rounded-full bg-gray-200" />
      <div className="h-6 w-20 rounded-md bg-gray-200" />
      <div className="h-12 w-12 rounded-full bg-gray-200" />
    </div>
    <div className="flex items-center justify-center gap-4 mt-8">
      <div className="h-9 w-32 rounded-lg bg-gray-200" />
      <div className="h-9 w-32 rounded-lg bg-gray-200" />
      <div className="h-9 w-32 rounded-lg bg-gray-200" />
    </div>
  </div>
);

const FilterModal = ({ isOpen, onClose, keyConcepts, applyFilters, initialFilters }) => {
  const [starredOnly, setStarredOnly] = useState(initialFilters.starredOnly);
  const [selectedConcepts, setSelectedConcepts] = useState(initialFilters.selectedConcepts);

  useEffect(() => {
    setStarredOnly(initialFilters.starredOnly);
    setSelectedConcepts(initialFilters.selectedConcepts);
  }, [isOpen, initialFilters]);

  if (!isOpen) return null;

  const handleApply = () => {
    applyFilters({ starredOnly, selectedConcepts });
    onClose();
  };

  const toggleConcept = (concept) => {
    setSelectedConcepts(prev =>
      prev.includes(concept) ? prev.filter(c => c !== concept) : [...prev, concept]
    );
  };

  return (
    <div className="fixed inset-0 bg-opacity-70 bg-black z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-md max-h-[90vh] flex flex-col">
        <div className="flex justify-between items-center mb-4 px-6 pt-6">
          <h2 className="text-lg font-semibold flex items-center">
            <Filter size={20} className="mr-2" />
            Filter Options
          </h2>
          <button onClick={onClose}><X size={24} /></button>
        </div>

        <div className="px-6 mb-4">
          <div className="flex items-center justify-between p-3 border rounded-lg">
            <label className="flex items-center cursor-pointer">
              <Star size={18} className="mr-2 text-yellow-600" /> Starred only
            </label>
            <label className="relative inline-flex items-center cursor-pointer">
              <input type="checkbox" checked={starredOnly} onChange={() => setStarredOnly(!starredOnly)} className="sr-only peer"/>
              <div className="w-11 h-6 bg-gray-200 rounded-full peer peer-focus:ring-2 peer-focus:ring-blue-300 peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-0.5 after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-black"></div>
            </label>
          </div>
        </div>

        {/* Scrollable body (mobile) */}
        <div className="px-6 pb-4 flex flex-col gap-4 overflow-hidden">
          <div className="flex flex-col flex-1">
            <h3 className="text-sm font-semibold mb-2 text-gray-700 shrink-0">Filter by Key concepts</h3>
            <div className="border rounded-lg flex flex-col overflow-hidden flex-1 md:max-h-80">
              <div className="p-2 border-b shrink-0 bg-white">
                <input type="text" placeholder="Search key concepts" className="w-full focus:outline-none text-sm"/>
              </div>
              <div className="flex-1 overflow-y-auto px-2 py-1 max-h-[50vh] md:max-h-full">{/* mobile constrained height */}
                {keyConcepts.map(concept => (
                  <label key={concept} htmlFor={`concept-${concept}`} className="flex items-center p-2 hover:bg-gray-100 rounded-md cursor-pointer text-sm">
                    <input type="checkbox" id={`concept-${concept}`} checked={selectedConcepts.includes(concept)} onChange={() => toggleConcept(concept)} className="mr-3 h-4 w-4 rounded border-gray-300 text-black focus:ring-black"/>
                    <span className="truncate">{concept}</span>
                  </label>
                ))}
                {!keyConcepts.length && (
                  <div className="text-xs text-gray-500 p-2">No key concepts.</div>
                )}
              </div>
            </div>
          </div>
        </div>

        <div className="mt-auto px-6 pb-6 pt-4 border-t pb-4 flex justify-end gap-3 bg-white">
          <button onClick={onClose} className="px-5 py-2 rounded-lg border font-semibold">Cancel</button>
            <button onClick={handleApply} className="px-5 py-2 rounded-lg bg-black text-white font-semibold">Apply</button>
        </div>
      </div>
    </div>
  );
};

// --- MAIN COMPONENT ---

export default function FlashcardsSection({ contentId }) {
  const [flashcards, setFlashcards] = useState([]);
  const [filteredCards, setFilteredCards] = useState([]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [showAnswer, setShowAnswer] = useState(false);
  const [showHintText, setShowHintText] = useState(false);
  const [starredCards, setStarredCards] = useState(new Set());
  const [isShuffled, setIsShuffled] = useState(false);
  const [showFilterModal, setShowFilterModal] = useState(false);
  const [isGenerating, setIsGenerating] = useState(false);
  const [generationError, setGenerationError] = useState(null);
  const [filters, setFilters] = useState({ starredOnly: false, selectedConcepts: [] });

  // useLimits for flashcards (mirror QuizSection)
  const { flashcards: flashcardsLimit } = useLimits();

  const keyConcepts = useMemo(() => [...new Set(flashcards.map(c => c.keyConcept))], [flashcards]);

  // Replace local generator with a direct backend call handler
  const handleGenerateNewCards = useCallback(async () => {
    // respect limit
    if (flashcardsLimit !== undefined && flashcardsLimit <= 0) {
      setGenerationError("You have reached your daily flashcard generation limit.");
      return;
    }
    // avoid duplicate in-flight
    if (contentId && isFlashcardsRequestInFlight(contentId)) return;

    setIsGenerating(true);
    setGenerationError(null);
    try {
      const generatedCards = await generateFlashcardsForSession(contentId);
      setFlashcards(generatedCards);
      setFilteredCards(generatedCards);
      setCurrentIndex(0);
      setShowAnswer(false);
      setShowHintText(false);
      setStarredCards(new Set());
    } catch (error) {
      setGenerationError("Failed to generate flashcards. Please try again.");
    } finally {
      setIsGenerating(false);
    }
  }, [contentId, flashcardsLimit]);

  // On mount: load saved flashcards first. Auto-generate only if none found (and not already in-flight)
  useEffect(() => {
    let mounted = true;
    const init = async () => {
      if (!contentId) return;
      setIsGenerating(true);
      try {
        const saved = await fetchLatestFlashcards(contentId);
        if (mounted && Array.isArray(saved) && saved.length > 0) {
          setFlashcards(saved);
          setFilteredCards(saved);
          setCurrentIndex(0);
          setShowAnswer(false);
          setShowHintText(false);
          setIsGenerating(false);
          return;
        }
      } catch (e) {}

      if (contentId && isFlashcardsRequestInFlight(contentId)) return;
      if (mounted) await handleGenerateNewCards();
    };
    init();
    return () => { mounted = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [contentId]);

  useEffect(() => {
    let cards = [...flashcards];
    if (filters.starredOnly) cards = cards.filter(c => starredCards.has(c.id));
    if (filters.selectedConcepts.length) cards = cards.filter(c => filters.selectedConcepts.includes(c.keyConcept));
    if (isShuffled) cards.sort(() => Math.random() - 0.5);
    setFilteredCards(cards);
    setCurrentIndex(0);
    setShowAnswer(false);
    setShowHintText(false);
  }, [flashcards, filters, isShuffled, starredCards]);

  const currentCard = filteredCards[currentIndex];
  const handleNext = () => { setShowAnswer(false); setShowHintText(false); setCurrentIndex((p) => (p + 1) % filteredCards.length); };
  const handlePrev = () => { setShowAnswer(false); setShowHintText(false); setCurrentIndex((p) => (p - 1 + filteredCards.length) % filteredCards.length); };
  const toggleStar = (e, cardId) => { e.stopPropagation(); setStarredCards(p => { const n = new Set(p); n.has(cardId) ? n.delete(cardId) : n.add(cardId); return n; }); };

  if (isGenerating) return <div className="min-h-screen flex items-center justify-center"><FlashcardSkeleton /></div>;
  if (generationError) return (
    <div className="min-h-screen flex flex-col items-center justify-center">
      <p>{generationError}</p>
      <button onClick={handleGenerateNewCards} className="mt-4 px-4 py-2 bg-black text-white rounded-lg">Try Again</button>
    </div>
  );

  return (
    <div className="min-h-screen p-4 md:p-8 flex flex-col items-center mb-10">
      {!currentCard ? (
        <div className="text-center">
          <p>No cards found.</p>
          <button onClick={handleGenerateNewCards} className="mt-4 px-4 py-2 bg-black text-white rounded-lg">Generate Cards</button>
        </div>
      ) : (
        <div className="w-full max-w-3xl">
          <div
            className="bg-white rounded-xl shadow-md p-8 w-full flex items-center justify-center text-center relative cursor-pointer border border-gray-200"
            style={{ perspective: "1000px", minHeight: "550px" }}
            onClick={() => { setShowAnswer(!showAnswer); setShowHintText(false); }}
          >
            <button onClick={(e) => { e.stopPropagation(); setShowHintText(!showHintText); }} className="absolute top-4 left-4 text-gray-500 text-sm font-medium cursor-pointer" disabled={showAnswer || !currentCard.hint}>Hint</button>
            <div className="absolute top-4 right-4 flex">
              <button onClick={(e) => toggleStar(e, currentCard.id)}><Star size={20} className={starredCards.has(currentCard.id) ? "text-yellow-600 fill-current" : "text-gray-400"} /></button>
              {/* Removed edit/manage button */}
            </div>
            <div className="transition-transform duration-500 w-full h-full" style={{ transformStyle: "preserve-3d", transform: showAnswer ? "rotateY(180deg)" : "rotateY(0deg)" }}>
              <div className="absolute inset-0 flex flex-col items-center justify-center p-4" style={{ backfaceVisibility: "hidden" }}>
                <p className="text-2xl">{currentCard.question}</p>
                {showHintText && currentCard.hint && <p className="mt-8 text-xl text-gray-500 italic">{currentCard.hint}</p>}
              </div>
              <div className="absolute inset-0 flex items-center justify-center p-4" style={{ backfaceVisibility: "hidden", transform: "rotateY(180deg)" }}>
                <p className="text-2xl">{currentCard.answer}</p>
              </div>
            </div>
          </div>
          <div className="flex items-center justify-between mt-6">
            <button onClick={handlePrev} className="p-3 rounded-full bg-white border shadow-sm enabled:hover:bg-gray-100 disabled:opacity-50" disabled={filteredCards.length < 2}><ChevronLeft size={20}/></button>
            <div className="font-semibold text-gray-600">{currentIndex + 1} / {filteredCards.length}</div>
            <button onClick={handleNext} className="p-3 rounded-full bg-white border shadow-sm enabled:hover:bg-gray-100 disabled:opacity-50" disabled={filteredCards.length < 2}><ChevronRight size={20}/></button>
          </div>
          <div className="flex items-center justify-center gap-4 mt-8">
            {/* Removed Manage Cards button */}
            <button onClick={() => setShowFilterModal(true)} className="text-sm font-medium text-gray-600 flex items-center gap-2 p-2 rounded-lg hover:bg-gray-200"><Filter size={16}/> Filter Options</button>
            <button onClick={() => setIsShuffled(!isShuffled)} className="text-sm font-medium text-gray-600 flex items-center gap-2 p-2 rounded-lg hover:bg-gray-200"><Shuffle size={16}/> Shuffle</button>
            {/* Removed New Cards button */}
          </div>
        </div>
      )}
      <FilterModal isOpen={showFilterModal} onClose={() => setShowFilterModal(false)} keyConcepts={keyConcepts} applyFilters={setFilters} initialFilters={filters} />
    </div>
  );
}
